import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Crown, ArrowLeft, ArrowRight, LogIn, Sparkles, Gift } from 'lucide-react';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';

export const hasAccess = (user) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const sub = user.subscription || {};
  if (!sub.plan_id) return false;
  if (sub.status && sub.status !== 'active') return false;
  if (sub.expires_at) {
    try {
      const exp = new Date(sub.expires_at);
      if (!isNaN(exp.getTime()) && exp < new Date()) return false;
    } catch {}
  }
  return true;
};

const LockedDialog = ({ open, onClose }) => {
  const { lang, isRTL } = useLang();
  const { user } = useAuth();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const L = (ar, en) => tx(lang, ar, en);
  const isGuest = !user;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-gradient-to-br from-blue-50 via-white to-slate-50 border-2 border-blue-200 text-white max-w-lg p-0 overflow-hidden">
        <div className="p-8 text-center relative">
          <div className="relative inline-flex mb-5">
            <div className="absolute inset-0 bg-amber-400/30 blur-2xl rounded-full" />
            <div className="relative w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-2xl shadow-amber-500/40">
              <Lock className="w-10 h-10 text-slate-900" strokeWidth={2.5} />
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-600 text-xs font-bold mb-3">
            <Crown className="w-3.5 h-3.5" />
            <span>{L('محتوى حصري للمشتركين', 'Subscribers Only')}</span>
          </div>

          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3">
            {isGuest ? L('قم بالاشتراك للوصول', 'Subscribe to Access') : L('يحتاج اشتراك فعّال', 'Active Subscription Required')}
          </h2>
          <p className="text-slate-600 text-sm md:text-base mb-6 leading-relaxed">
            {L('احصل على وصول كامل لجميع الكورسات والفيديوهات والمقالات الحصرية', 'Get full access to all exclusive courses, videos, and articles')}
          </p>

          <div className="grid grid-cols-3 gap-2 mb-6">
            {[
              { Ic: Sparkles, ar: '+70 كورس', en: '+70 Courses' },
              { Ic: Crown, ar: 'شهادة معتمدة', en: 'Certificate' },
              { Ic: Lock, ar: 'محتوى حصري', en: 'Exclusive' },
            ].map((f, i) => (
              <div key={i} className="rounded-lg bg-slate-100 border border-slate-200 p-2.5">
                <f.Ic className="w-4 h-4 text-amber-600 mx-auto mb-1" />
                <div className="text-slate-600 text-[11px]">{lang === 'ar' ? f.ar : f.en}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Link to="/subscription" onClick={onClose} data-testid="locked-dialog-subscribe" className="w-full sm:flex-1 btn-subscribe h-12">
                <Crown className="w-4 h-4" />
                {L('اشترك الآن', 'Subscribe Now')}
                <Arrow className="w-4 h-4" />
            </Link>
            {isGuest && (
              <Link to="/login" onClick={onClose} className="flex-1">
                <Button variant="outline" className="w-full border-blue-200 text-slate-800 hover:bg-slate-100 h-12 bg-transparent">
                  <LogIn className="w-4 h-4 me-2" />
                  {L('دخول', 'Log In')}
                </Button>
              </Link>
            )}
          </div>

          <div className="mt-4 text-xs text-slate-500">
            {L('ضمان استرداد 7 أيام • إلغاء في أي وقت', '7-day refund • Cancel anytime')}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const isOpen = (item, user) => hasAccess(user) || !!item?.is_free;

export const FeaturedBadge = ({ id, className = '' }) => {
  const { lang } = useLang();
  return (
    <span data-testid={`course-featured-badge-${id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400 text-slate-900 text-[11px] sm:text-[11px] font-extrabold shadow-sm ${className}`}>
      <Crown className="w-3 h-3" />{tx(lang, 'مميز', 'Featured')}
    </span>
  );
};

export const FreeBadge = ({ className = '' }) => {
  const { lang } = useLang();
  return (
    <span data-testid="free-badge" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[11px] sm:text-[11px] font-extrabold shadow-sm ${className}`}>
      <Gift className="w-3 h-3" />{tx(lang, 'مجاني', 'Free')}
    </span>
  );
};

export const FreeOnlyToggle = ({ value, onChange, testId = 'free-only-toggle' }) => {
  const { lang } = useLang();
  return (
    <button type="button" data-testid={testId} onClick={() => onChange(!value)} className={`px-3.5 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 border transition-colors ${value ? 'bg-emerald-500 border-emerald-500 text-white shadow-md' : 'bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50'}`}>
      <Gift className="w-3.5 h-3.5" />{tx(lang, 'مجاني فقط', 'Free only')}
    </button>
  );
};

export default LockedDialog;
